/**
 * moduleCatalog — the single source of truth for the Store / marketplace surface.
 *
 * SHARED CONTRACT (Phase 5 "mechanism, not prices"):
 *   Each entry: { id, name, blurb, icon, category, access, requiredTier, status }
 *     - id        : stable module id. FEATURE ids mirror useUserStore MODULE_DEFAULTS 1:1.
 *     - name      : display label.
 *     - blurb     : one-line description shown on the Store card.
 *     - icon      : a lucide-react icon NAME string (resolved by IconRenderer / ICON_MAP).
 *     - category  : 'feature' | 'integration'.
 *     - access    : 'free' | 'paid'.
 *     - requiredTier : null | 'pro' | 'premium' | 'founder' — the minimum tier a
 *                      'paid' module needs. Always null for 'free' modules.
 *     - status    : 'available' | 'coming-soon'.
 *
 * NO LOCKOUTS TODAY: every FEATURE entry ships access:'free', requiredTier:null,
 * status:'available'. This preserves current behavior exactly — nothing is gated.
 * The paid machinery exists but is UNUSED by default; the free/paid split is config
 * the owner flips LATER by editing entries here (no code change required).
 *
 * INTEGRATIONS (notion, obsidian) are NOT in MODULE_DEFAULTS — their sync engine is
 * deferred, so they ship status:'coming-soon' and are not enableable yet. Their
 * access carries a forward-looking 'paid' label but nothing enforces it while
 * they are coming-soon.
 */

// Tier ordering for entitlement comparisons. Higher rank satisfies a lower requiredTier.
export const TIER_RANK = Object.freeze({
    free: 0,
    pro: 1,
    premium: 2,
    founder: 3,
});

// ── Feature modules — ids MUST mirror useUserStore MODULE_DEFAULTS exactly ──
// All free/available today (no lockouts). Icons are lucide names already used in the
// Sidebar so the Store matches the nav.
const FEATURE_MODULES = [
    {
        id: 'dashboard',
        name: 'Dashboard',
        blurb: 'Analytics overview with charts and trends.',
        icon: 'LayoutDashboard',
        category: 'feature',
        access: 'free',
        requiredTier: null,
        status: 'available',
    },
    {
        id: 'projects',
        name: 'Projects',
        blurb: 'Multi-task projects with statuses and progress.',
        icon: 'Folder',
        category: 'feature',
        access: 'free',
        requiredTier: null,
        status: 'available',
    },
    {
        id: 'areas',
        name: 'Areas',
        blurb: 'Ongoing areas of responsibility.',
        icon: 'MapPin',
        category: 'feature',
        access: 'free',
        requiredTier: null,
        status: 'available',
    },
    {
        id: 'calendar',
        name: 'Calendar',
        blurb: 'Time-blocked calendar and ideal-week planning.',
        icon: 'CalendarDays',
        category: 'feature',
        access: 'free',
        requiredTier: null,
        status: 'available',
    },
    {
        id: 'quests',
        name: 'Quests',
        blurb: 'Long-horizon goals broken into milestones.',
        icon: 'Map',
        category: 'feature',
        access: 'free',
        requiredTier: null,
        status: 'available',
    },
    {
        id: 'vision',
        name: 'Vision',
        blurb: 'Vision board and life direction.',
        icon: 'Compass',
        category: 'feature',
        access: 'free',
        requiredTier: null,
        status: 'available',
    },
    {
        id: 'rituals',
        name: 'Rituals',
        blurb: 'Habits and recurring routines with streaks.',
        icon: 'Repeat',
        category: 'feature',
        access: 'free',
        requiredTier: null,
        status: 'available',
    },
    {
        id: 'resources',
        name: 'Resources',
        blurb: 'Saved references, notes, and reading.',
        icon: 'BookOpen',
        category: 'feature',
        access: 'free',
        requiredTier: null,
        status: 'available',
    },
    {
        id: 'reviews',
        name: 'Reviews',
        blurb: 'Weekly and periodic review workflows.',
        icon: 'RotateCw',
        category: 'feature',
        access: 'free',
        requiredTier: null,
        status: 'available',
    },
    {
        id: 'archives',
        name: 'Archives',
        blurb: 'Completed and archived items.',
        icon: 'Archive',
        category: 'feature',
        access: 'free',
        requiredTier: null,
        status: 'available',
    },
    {
        // Not a page — a sidebar capability. Off ⇒ the nav is flat and
        // header-less (the default). On ⇒ a "Customise nav" control appears in
        // the sidebar header for building your own named groups.
        id: 'navGroups',
        name: 'Custom Nav Groups',
        blurb: 'Organise the sidebar your way — name your own groups.',
        icon: 'ListTree',
        category: 'feature',
        access: 'free',
        requiredTier: null,
        status: 'available',
    },
    {
        // Not a page — a top-bar capability. Off ⇒ no filter control renders and
        // nothing is filtered (the default). On ⇒ a segmented control appears in
        // the header. Ships as Work/Life; the id is deliberately generic because
        // the roadmap is filters you define yourself, not just by area.
        id: 'contextFilter',
        name: 'Context Filters',
        blurb: 'Narrow the whole app to one context — Work or Life today, filters you define next.',
        icon: 'Filter',
        category: 'feature',
        access: 'free',
        requiredTier: null,
        status: 'available',
    },
];

// ── Integration plugins — NOT in MODULE_DEFAULTS; sync deferred (coming-soon) ──
const INTEGRATION_MODULES = [
    {
        id: 'notion',
        name: 'Notion',
        blurb: 'Two-way sync with your Notion workspace.',
        icon: 'FileText',
        category: 'integration',
        access: 'paid',
        requiredTier: 'pro',
        status: 'coming-soon',
    },
    {
        id: 'obsidian',
        name: 'Obsidian',
        blurb: 'Back your notes with a local Obsidian vault.',
        icon: 'Boxes',
        category: 'integration',
        access: 'paid',
        requiredTier: 'pro',
        status: 'coming-soon',
    },
];

// The full catalog: features first, then integrations.
export const MODULE_CATALOG = Object.freeze(
    [...FEATURE_MODULES, ...INTEGRATION_MODULES].map((e) => Object.freeze(e))
);

// ── Runtime config overlay (config/public → moduleCatalog section) ──
//
// useConfigStore registers a getter here at import time (registration keeps this
// module dependency-free: tests that import only the catalog see pure code
// defaults). When registered AND the projected config carries a moduleCatalog
// section, the helpers below overlay the per-module BUSINESS knobs — access /
// requiredTier / status / stripePriceId — over the frozen code entries. Identity
// fields (id/name/blurb/icon/category) always come from code. Absent getter,
// absent config, absent section, or absent key ⇒ the exact code-default entry
// (same frozen object), so first paint + offline never regress.

let _publicConfigGetter = null;

/** @internal Registered by useConfigStore; pass null to detach (tests). */
export function __setPublicConfigGetter(fn) {
    _publicConfigGetter = typeof fn === 'function' ? fn : null;
}

// Only the business knobs may be overlaid — never identity/display fields.
const OVERLAYABLE_KEYS = ['access', 'requiredTier', 'status', 'stripePriceId'];

function catalogOverlay() {
    if (!_publicConfigGetter) return null;
    try {
        const overlay = _publicConfigGetter()?.moduleCatalog;
        return overlay && typeof overlay === 'object' ? overlay : null;
    } catch {
        return null; // any error → pure code defaults
    }
}

/** Merge the runtime overlay (if any) over a frozen code entry. */
function applyOverlay(entry, overlay) {
    const patch = overlay?.[entry.id];
    if (!patch || typeof patch !== 'object') return entry;
    let out = null;
    for (const key of OVERLAYABLE_KEYS) {
        if (patch[key] !== undefined && patch[key] !== entry[key]) {
            if (!out) out = { ...entry };
            out[key] = patch[key];
        }
    }
    return out || entry;
}

// ── Helpers ──

/** Look up a single catalog entry by id. Returns undefined for un-catalogued ids. */
export function getCatalogEntry(id) {
    const entry = MODULE_CATALOG.find((e) => e.id === id);
    if (!entry) return undefined;
    return applyOverlay(entry, catalogOverlay());
}

/** All feature-category entries (mirror MODULE_DEFAULTS). */
export function featureModules() {
    const overlay = catalogOverlay();
    return MODULE_CATALOG.filter((e) => e.category === 'feature').map((e) => applyOverlay(e, overlay));
}

/** All integration-category entries (notion, obsidian). */
export function integrationModules() {
    const overlay = catalogOverlay();
    return MODULE_CATALOG.filter((e) => e.category === 'integration').map((e) => applyOverlay(e, overlay));
}
